import { Capacitor } from '@capacitor/core'

/**
 * Initializes native Capacitor plugins when running on iOS/Android.
 * No-ops on web — safe to call in all environments.
 */
export async function initNativePlugins() {
  if (!Capacitor.isNativePlatform()) return

  await Promise.all([
    initStatusBar(),
    initSplashScreen(),
    initKeyboard(),
  ])
}

// Status-bar background per theme (kept in sync with --color-bg in
// src/styles/index.css and THEME_COLORS in ThemeContext.jsx).
const STATUS_BAR_BG = { dark: '#0f111a', light: '#f5f6fa' }

async function initStatusBar() {
  await applyStatusBarTheme(readInitialTheme())
}

function readInitialTheme() {
  try {
    const stored = localStorage.getItem('nudgu:theme')
    return stored === 'light' ? 'light' : 'dark'
  } catch {
    return 'dark'
  }
}

/**
 * Updates the native iOS/Android status bar to match the active theme.
 * No-ops on web — safe to call in all environments.
 * Style.Dark = dark (light-colored) status-bar content for dark backgrounds;
 * Style.Light = light (dark-colored) content for light backgrounds.
 */
export async function applyStatusBarTheme(theme) {
  if (!Capacitor.isNativePlatform()) return
  const { StatusBar, Style } = await import('@capacitor/status-bar')
  await StatusBar.setStyle({ style: theme === 'light' ? Style.Light : Style.Dark })
  await StatusBar.setBackgroundColor({ color: STATUS_BAR_BG[theme] ?? STATUS_BAR_BG.dark })
}

async function initSplashScreen() {
  const { SplashScreen } = await import('@capacitor/splash-screen')
  // Auto-hide after a short delay so the app content is rendered
  await SplashScreen.hide({ fadeOutDuration: 300 })
}

async function initKeyboard() {
  const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard')
  // Use None so we control positioning ourselves via --keyboard-height
  await Keyboard.setResizeMode({ mode: KeyboardResize.None })
  await Keyboard.setScroll({ isDisabled: false })

  Keyboard.addListener('keyboardWillShow', (info) => {
    document.documentElement.style.setProperty(
      '--keyboard-height',
      `${info.keyboardHeight}px`,
    )
  })
  Keyboard.addListener('keyboardWillHide', () => {
    document.documentElement.style.setProperty('--keyboard-height', '0px')
  })
}

